import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { take } from 'rxjs/operators';
import { ApiError, ButtonLoader, FormsUtilsService, MessageService, notCloseMessageOptions } from '../../../../../core';
import { Campanha } from '../../campanha';
import { CampanhaSearch } from '../../campanha-search';
import { CampanhaCrudHttpService } from '../../services';
import { CampanhaEnviarAnalise } from '../campanha-enviar-analise';
import { CampanhaAnaliseHttpService } from '../services';

interface Config {
    title: string;
    subTitle: string;
    justifiticativaLabel: string;
    justifiticativaHelText: string;
    btnEnviarAnaliseIcon: string;
    btnEnviarAnaliseText: string;
    successMessage: string;
    errorMessage: string;
}

@Component({
    selector: 'sgt-campanha-enviar-analise-form-modal',
    templateUrl: './campanha-enviar-analise-form-modal.component.html',
    styleUrls: ['./campanha-enviar-analise-form-modal.component.less']
})
export class CampanhaEnviarAnaliseFormModalComponent implements OnInit {
    config: Config;
    idCampanha: number;
    campanha: Campanha;
    isLoadingCampanha: boolean;

    entity: CampanhaEnviarAnalise;
    elModal: any;

    @Output() afterEnviarAnalise = new EventEmitter<CampanhaSearch>();

    @ViewChild('form') form: NgForm;
    @ViewChild('justificativaAnaliseTextArea') justificativaAnaliseTextArea: ElementRef;

    constructor(private el: ElementRef,
                private messageService: MessageService,
                private formsUtilsService: FormsUtilsService,
                private campanhaCrudHttpService: CampanhaCrudHttpService,
                private campanhaAnaliseHttpService: CampanhaAnaliseHttpService) { }

    ngOnInit() {
        this.create();
    }

    /** Cria o modal */
    private create() {
        this.elModal = jQuery(this.el.nativeElement).find('.modal') as any;
        this.elModal
            .on('show.bs.modal', (e: any) => {
                e.stopPropagation();
            })
            .on('shown.bs.modal', (e: any) => {
                this.afterOpen();
                e.stopPropagation();
            })
            .on('hide.bs.modal', (e: any) => {
                this.clear();
                e.stopPropagation();
            })
            .on('hidden.bs.modal', (e: any) => {
                e.stopPropagation();
            });
    }

    private loadCampanha() {
        this.isLoadingCampanha = true;
        this.campanhaCrudHttpService.find(this.idCampanha)
            .pipe(
                take(1)
            )
            .subscribe(
                campanha => {
                    this.campanha = campanha;
                },
                (error: ApiError) => {
                    this.messageService.error(error.cause, error.message, error);
                    this.close();
                },
                () => this.isLoadingCampanha = false
            );
    }

    /** Abre o modal para aprovar a análise da campanha */
    aprovar(idCampanha: number) {
        this.idCampanha = idCampanha;
        this.entity = new CampanhaEnviarAnalise(true);
        this.open();
    }

    /** Abre o modal para reprovar a análise da campanha */
    reprovar(idCampanha: number) {
        this.idCampanha = idCampanha;
        this.entity = new CampanhaEnviarAnalise(false);
        this.open();
    }

    private clear() {
        this.config = null;
        this.idCampanha = null;
        this.campanha = null;
        this.isLoadingCampanha = false;
        this.entity = null;
    }

    /** Abre o modal */
    private open() {
        if (this.entity.aprovada) {
            this.config = {
                title: 'Aprovar Campanha',
                subTitle: 'Informe a justificativa da aprovação desta campanha',
                justifiticativaLabel: 'Justificativa da Aprovação',
                justifiticativaHelText: 'Informe a justificativa da aprovação desta campanha com pelo menos 15 caracteres',
                btnEnviarAnaliseIcon: 'far fa-thumbs-up',
                btnEnviarAnaliseText: 'Aprovar Campanha',
                successMessage: 'Campanha aprovada com sucesso',
                errorMessage: 'Erro ao aprovar a campanha'
            };
        } else {
            this.config = {
                title: 'Reprovar Campanha',
                subTitle: 'Informe a justificativa da reprovação desta campanha',
                justifiticativaLabel: 'Justificativa da Reprovação',
                justifiticativaHelText: 'Informe a justificativa da reprovação desta campanha com pelo menos 15 caracteres',
                btnEnviarAnaliseIcon: 'far fa-thumbs-down',
                btnEnviarAnaliseText: 'Reprovar Campanha',
                successMessage: 'Campanha reprovada com sucesso',
                errorMessage: 'Erro ao reprovar a campanha'
            };
        }

        this.elModal.modal({keyboard: false, backdrop: 'static'});
    }

    /** Ações após abrir o modal */
    private afterOpen() {
        this.loadCampanha();
        // Foca o textarea da justificativa da análise
        setTimeout(() => {
            this.justificativaAnaliseTextArea.nativeElement.focus();
        }, 500);
    }

    /** Fecha o modal */
    close() {
        this.elModal.modal('hide');
    }

    /** Verifica se o envio da análise da campanha está válido */
    private isValid(): boolean {
        this.formsUtilsService.makeAllFieldsDirty(this.form);

        // Justifitiva não informada ou menor que 15 caracteres
        if (!this.entity.justificativaAnalise || this.entity.justificativaAnalise.trim().length < 15) {
            this.messageService.warning('Não é possível enviar a análise da campanha por que a justificativa da análise não foi informada ou é menor que 15 caracteres. Informe a justificativa da sua análise com pelo menos 15 caracteres e tente novamente', 'Atenção', notCloseMessageOptions);
            return false;
        }

        // Form inválido
        if (this.form.invalid) {
            this.formsUtilsService.showMsgFormInvalid();
            return false;
        }

        return true;
    }

    /** Enviar a análise da campanha */
    enviarAnalise() {
        if (!this.isValid()) {
            return false;
        }

        const btn = new ButtonLoader('#btnEnviarAnalise');
        btn.loading(this.entity.aprovada ? 'Aprovando' : 'Reprovando');
        this.campanhaAnaliseHttpService.enviar(this.idCampanha, this.entity)
            .pipe(
                take(1)
            )
            .subscribe(
                campanha => {
                    this.messageService.success('', this.config.successMessage);
                    this.afterEnviarAnalise.emit(campanha);
                    this.close();
                    btn.reset();
                },
                (error: ApiError) => {
                    this.messageService.error(error.message, this.config.errorMessage, error);
                    btn.reset();
                },
            );
    }
}
